import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Button } from '@/components/ui/button';
import { User, Ruler, Scale, Target, Mail, X } from 'lucide-react';

const POSITIONS = [
  { id: 'pg', name: 'Point Guard (1)' },
  { id: 'sg', name: 'Shooting Guard (2)' },
  { id: 'sf', name: 'Small Forward (3)' },
  { id: 'pf', name: 'Power Forward (4)' },
  { id: 'c', name: 'Center (5)' },
];

export default function ProfileCompletionModal({ user, userData, onComplete }) {
  const [formData, setFormData] = useState({
    height: userData?.height || '',
    weight: userData?.weight || '',
    position: userData?.position || '',
    email: userData?.email || user?.email || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Check if profile is complete
  const isComplete = Boolean(
    formData.height && 
    formData.weight && 
    formData.position && 
    formData.email
  );

  // Get missing fields for display
  const getMissingFields = () => {
    const missing = [];
    if (!formData.height) missing.push('Größe');
    if (!formData.weight) missing.push('Gewicht');
    if (!formData.position) missing.push('Position');
    if (!formData.email) missing.push('E-Mail');
    return missing;
  };

  const handleSave = async () => {
    if (!isComplete) {
      setError('Bitte fülle alle Felder aus');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        height: parseInt(formData.height),
        weight: parseInt(formData.weight),
        position: formData.position,
        email: formData.email,
        profile_completed: true,
        profile_updated_at: new Date()
      });

      onComplete();
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Fehler beim Speichern. Bitte versuche es erneut.');
    }

    setSaving(false);
  };

  const missingFields = getMissingFields();

  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4">
      <div className="bg-[#12090c] border border-pink-500/30 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
            <User className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Profil vervollständigen</h2>
            <p className="text-sm text-rose-200/60">
              {missingFields.length > 0 
                ? `Noch ${missingFields.length} Felder offen`
                : 'Alle Daten vorhanden!'
              }
            </p>
          </div>
        </div>

        {/* Info */}
        <div className="bg-blue-600/10 border border-blue-500/30 rounded-lg p-3 mb-6">
          <p className="text-sm text-blue-200">
            ℹ️ Diese Daten helfen uns, die App für dich zu verbessern und Team-Statistiken zu erstellen.
          </p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Height */}
          <div>
            <label className="flex items-center gap-2 text-sm text-rose-200/70 mb-2">
              <Ruler className="w-4 h-4" />
              Größe (cm)
              {!formData.height && <span className="text-red-400">*</span>}
            </label>
            <input
              type="number"
              value={formData.height}
              onChange={(e) => setFormData({...formData, height: e.target.value})}
              placeholder="z.B. 185"
              className="w-full bg-[#0d0409] border border-white/20 rounded-lg px-4 py-3 text-white placeholder-rose-200/30 focus:outline-none focus:border-pink-500/50"
              min="140"
              max="230"
              data-testid="profile-height"
            />
          </div>

          {/* Weight */}
          <div>
            <label className="flex items-center gap-2 text-sm text-rose-200/70 mb-2">
              <Scale className="w-4 h-4" />
              Gewicht (kg)
              {!formData.weight && <span className="text-red-400">*</span>}
            </label>
            <input
              type="number"
              value={formData.weight}
              onChange={(e) => setFormData({...formData, weight: e.target.value})}
              placeholder="z.B. 80"
              className="w-full bg-[#0d0409] border border-white/20 rounded-lg px-4 py-3 text-white placeholder-rose-200/30 focus:outline-none focus:border-pink-500/50"
              min="40"
              max="200"
              data-testid="profile-weight"
            />
          </div>

          {/* Position */}
          <div>
            <label className="flex items-center gap-2 text-sm text-rose-200/70 mb-2">
              <Target className="w-4 h-4" />
              Lieblingsposition
              {!formData.position && <span className="text-red-400">*</span>}
            </label>
            <select
              value={formData.position}
              onChange={(e) => setFormData({...formData, position: e.target.value})}
              className="w-full bg-[#0d0409] border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-pink-500/50"
              data-testid="profile-position"
            >
              <option value="">Position wählen...</option>
              {POSITIONS.map(pos => (
                <option key={pos.id} value={pos.id}>{pos.name}</option>
              ))}
            </select>
          </div>

          {/* Email */}
          <div>
            <label className="flex items-center gap-2 text-sm text-rose-200/70 mb-2">
              <Mail className="w-4 h-4" />
              E-Mail Adresse
              {!formData.email && <span className="text-red-400">*</span>}
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="deine@email.de"
              className="w-full bg-[#0d0409] border border-white/20 rounded-lg px-4 py-3 text-white placeholder-rose-200/30 focus:outline-none focus:border-pink-500/50"
              data-testid="profile-email"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mt-4 p-3 bg-red-600/20 border border-red-500/30 rounded-lg text-red-300 text-sm">
            {error}
          </div>
        )}

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving || !isComplete}
          className="w-full mt-6 py-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 disabled:opacity-50"
          data-testid="profile-save-btn"
        >
          {saving ? 'Speichern...' : isComplete ? 'Profil speichern' : `Noch ${missingFields.length} Felder ausfüllen`}
        </Button>

        {/* Skip hint */}
        <p className="text-center text-xs text-rose-200/40 mt-3">
          Du kannst die App erst nutzen, wenn alle Daten ausgefüllt sind.
        </p>
      </div>
    </div>
  );
}
